/**
 * Employment Info Form Component
 *
 * Form for managing employment information in financial profile.
 */

import { Box, Grid, MagicInput, MagicSelect, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { useMemo } from 'react';
import {
  BUSINESS_TYPE_OPTIONS,
  EMPLOYMENT_STATUS_OPTIONS,
  type EmploymentInfo,
} from '@/lib/constants/financial-profile';

interface EmploymentInfoFormProps {
  employment: EmploymentInfo | undefined;
  onChange: (employment: Partial<EmploymentInfo>) => void;
  disabled?: boolean;
}

export default function EmploymentInfoForm({ employment, onChange, disabled = false }: EmploymentInfoFormProps) {
  const { t } = useI18n();
  const isEmployed = employment?.employmentStatus === 'employed';
  const isSelfEmployed = employment?.employmentStatus === 'self_employed';

  // Translate dropdown options using i18n keys
  const EMPLOYMENT_STATUS_I18N: Record<string, string> = {
    employed: 'employment.status.employed',
    self_employed: 'employment.status.selfEmployed',
    retired: 'employment.status.retired',
    unemployed: 'employment.status.unemployed',
    student: 'employment.status.student',
    other: 'employment.status.other',
  };
  const BUSINESS_TYPE_I18N: Record<string, string> = {
    sole_proprietorship: 'employment.businessType.soleProprietorship',
    llc: 'employment.businessType.llc',
    s_corp: 'employment.businessType.sCorp',
    c_corp: 'employment.businessType.cCorp',
    partnership: 'employment.businessType.partnership',
    other: 'employment.businessType.other',
  };
  const translatedEmploymentOptions = useMemo(
    () =>
      EMPLOYMENT_STATUS_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(EMPLOYMENT_STATUS_I18N[opt.value] ?? opt.label),
      })),
    [t],
  );
  const translatedBusinessOptions = useMemo(
    () =>
      BUSINESS_TYPE_OPTIONS.map((opt) => ({ value: opt.value, label: t(BUSINESS_TYPE_I18N[opt.value] ?? opt.label) })),
    [t],
  );

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('employment.description')}
      </Typography>

      <Grid container spacing={3}>
        {/* Employment Status */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicSelect
            fullWidth
            required
            disabled={disabled}
            label={t('employment.status')}
            value={employment?.employmentStatus ?? ''}
            onChange={(e) => onChange({ employmentStatus: e.target.value as EmploymentInfo['employmentStatus'] })}
            options={translatedEmploymentOptions}
          />
        </Grid>

        {/* Monthly Income */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicInput
            fullWidth
            label={t('employment.monthlyIncome')}
            type="number"
            value={employment?.monthlyIncome ?? ''}
            onChange={(e) => onChange({ monthlyIncome: Number.parseFloat(e.target.value) || 0 })}
            disabled={disabled}
            required
            slotProps={{
              input: {
                startAdornment: <span style={{ marginRight: 8 }}>$</span>,
              },
            }}
          />
        </Grid>

        {/* Employed Fields */}
        {isEmployed && (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                label={t('employment.employerName')}
                value={employment?.employerName ?? ''}
                onChange={(e) => onChange({ employerName: e.target.value })}
                disabled={disabled}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                label={t('employment.jobTitle')}
                value={employment?.jobTitle ?? ''}
                onChange={(e) => onChange({ jobTitle: e.target.value })}
                disabled={disabled}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                label={t('employment.yearsAtCurrentJob')}
                type="number"
                value={employment?.yearsAtCurrentJob ?? ''}
                onChange={(e) => onChange({ yearsAtCurrentJob: Number.parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                slotProps={{
                  input: {
                    inputProps: { min: 0, step: 0.5 },
                  },
                }}
              />
            </Grid>
          </>
        )}

        {/* Self-Employed Fields */}
        {isSelfEmployed && (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                label={t('employment.businessName')}
                value={employment?.businessName ?? ''}
                onChange={(e) => onChange({ businessName: e.target.value })}
                disabled={disabled}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicSelect
                fullWidth
                disabled={disabled}
                label={t('employment.businessType')}
                value={employment?.businessType ?? ''}
                onChange={(e) => onChange({ businessType: e.target.value as string })}
                options={translatedBusinessOptions}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                label={t('employment.yearsInBusiness')}
                type="number"
                value={employment?.yearsInBusiness ?? ''}
                onChange={(e) => onChange({ yearsInBusiness: Number.parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                slotProps={{
                  input: {
                    inputProps: { min: 0, step: 0.5 },
                  },
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
}
